import type { BraceletPatternDto, CellValue, HexColor, PatternRow } from '@miyuki/types';

import { ValidationError } from '../errors';

import { normalizeHexColor } from './palette';
import { MAX_COLORS, MIN_COLORS } from './pattern.constants';
import { assertColorIndex, type PatternEdit } from './pattern.entity';

/** Toda operación de paleta guarda un snapshot: cambia la forma del agregado. */
function snapshotEdit(pattern: BraceletPatternDto, next: BraceletPatternDto): PatternEdit {
  // La paleta no toca la cantidad de filas, ni siquiera al reindexar (RF-66).
  return { pattern: next, undo: { kind: 'snapshot', pattern, progressDelta: 0 } };
}

/**
 * Cambia un color de la paleta. La cuadrícula no se toca: las celdas guardan
 * índices, así que todas las mostacillas de ese color cambian solas.
 */
export function updateColor(
  pattern: BraceletPatternDto,
  index: number,
  color: HexColor,
): PatternEdit {
  assertColorIndex(pattern, index);
  const normalized = normalizeHexColor(color);

  const colors = pattern.palette.colors.map((current, position) =>
    position === index ? normalized : current,
  );

  return snapshotEdit(pattern, { ...pattern, palette: { ...pattern.palette, colors } });
}

/**
 * Agrega un color a la paleta.
 *
 * Falla al alcanzar el tope declarado en vez de ampliarlo por su cuenta: quien
 * llama es quien debe ofrecerle a la usuaria subir el límite (RF-17). El
 * dominio no toma esa decisión.
 */
export function addColor(pattern: BraceletPatternDto, color: HexColor): PatternEdit {
  const normalized = normalizeHexColor(color);
  const { colors, declaredCount } = pattern.palette;

  if (colors.length >= MAX_COLORS) {
    throw new ValidationError(`Un diseño no puede superar los ${MAX_COLORS} colores`, [
      { path: 'palette.colors', message: `Ya tiene ${colors.length}` },
    ]);
  }

  if (colors.length >= declaredCount) {
    throw new ValidationError(
      `El diseño declaró ${declaredCount} colores y ya están todos en uso`,
      [{ path: 'palette.declaredCount', message: `Declarados: ${declaredCount}` }],
    );
  }

  return snapshotEdit(pattern, {
    ...pattern,
    palette: { ...pattern.palette, colors: [...colors, normalized] },
  });
}

/**
 * Agrega un color subiendo el tope declarado si hace falta, en **una sola
 * operación**.
 *
 * Existe porque la alternativa —`raiseDeclaredColorCount` y luego `addColor`—
 * deja dos entradas en el historial, y la usuaria tendría que deshacer dos
 * veces algo que vivió como un solo acto. Quien decide ampliar el tope sigue
 * siendo la interfaz: `addColor` conserva su contrato de fallar al alcanzarlo.
 */
export function addColorRaisingDeclaredCount(
  pattern: BraceletPatternDto,
  color: HexColor,
): PatternEdit {
  const normalized = normalizeHexColor(color);
  const { colors, declaredCount } = pattern.palette;

  if (colors.length >= MAX_COLORS) {
    throw new ValidationError(`Un diseño no puede superar los ${MAX_COLORS} colores`, [
      { path: 'palette.colors', message: `Ya tiene ${colors.length}` },
    ]);
  }

  return snapshotEdit(pattern, {
    ...pattern,
    palette: {
      colors: [...colors, normalized],
      declaredCount: Math.max(declaredCount, colors.length + 1),
    },
  });
}

/** Sube el tope declarado de colores. Solo hacia arriba y nunca más allá de 99. */
export function raiseDeclaredColorCount(
  pattern: BraceletPatternDto,
  declaredCount: number,
): PatternEdit {
  const current = pattern.palette.declaredCount;

  if (!Number.isInteger(declaredCount) || declaredCount <= current || declaredCount > MAX_COLORS) {
    throw new ValidationError(`El tope de colores solo puede subir, hasta ${MAX_COLORS}`, [
      { path: 'palette.declaredCount', message: `Actual: ${current}, pedido: ${declaredCount}` },
    ]);
  }

  return snapshotEdit(pattern, {
    ...pattern,
    palette: { ...pattern.palette, declaredCount },
  });
}

/**
 * Cuántas mostacillas usan ese color. No modifica nada: la interfaz lo consulta
 * para escribir la advertencia exacta antes de pedir confirmación (RF-18).
 */
export function countBeadsUsingColor(pattern: BraceletPatternDto, index: number): number {
  assertColorIndex(pattern, index);

  return pattern.rows.reduce(
    (total, row) => total + row.filter((cell) => cell === index).length,
    0,
  );
}

/**
 * Quita un color de la paleta.
 *
 * Es la operación más delicada del agregado: al desaparecer una posición, todo
 * índice mayor se desplaza una casilla hacia abajo. `reassignTo` se interpreta
 * en la paleta **anterior** —es lo que la usuaria vio al elegirlo— y se traduce
 * a la nueva antes de escribirlo en las celdas.
 */
export function removeColor(
  pattern: BraceletPatternDto,
  index: number,
  reassignTo: number | null,
): PatternEdit {
  assertColorIndex(pattern, index);

  if (pattern.palette.colors.length <= MIN_COLORS) {
    throw new ValidationError('La paleta debe conservar al menos un color', [
      { path: 'palette.colors', message: 'Es el único color del diseño' },
    ]);
  }

  if (reassignTo !== null) {
    assertColorIndex(pattern, reassignTo);
    if (reassignTo === index) {
      throw new ValidationError('No se puede reasignar las mostacillas al color que se quita', [
        { path: 'reassignTo', message: `Índice: ${reassignTo}` },
      ]);
    }
  }

  /** Posición que ocupa, tras el borrado, un índice de la paleta anterior. */
  const shift = (old: number): number => (old < index ? old : old - 1);

  const target: CellValue = reassignTo === null ? null : shift(reassignTo);

  const rows: readonly PatternRow[] = pattern.rows.map((row) =>
    row.map((cell) => {
      if (cell === null) return null;
      return cell === index ? target : shift(cell);
    }),
  );

  const colors = pattern.palette.colors.filter((_color, position) => position !== index);

  return snapshotEdit(pattern, {
    ...pattern,
    rows,
    palette: {
      colors,
      declaredCount: Math.max(MIN_COLORS, colors.length, pattern.palette.declaredCount - 1),
    },
  });
}
